import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, FileEdit, Plus } from 'lucide-react';

interface KnowledgeBase {
  id: string;
  title: string;
  file_url?: string;
}

interface EditInSprintDialogProps {
  kb: KnowledgeBase;
  open: boolean;
  onClose: () => void;
}

export const EditInSprintDialog = ({ kb, open, onClose }: EditInSprintDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [objective, setObjective] = useState(`Edit and enhance ${kb.title}`);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: sprints, isLoading: sprintsLoading } = useQuery({
    queryKey: ['my-sprints', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('prompt_architecture_sprints')
        .select('*')
        .eq('creator_id', user.id)
        .eq('status', 'in_progress')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user && open
  });

  const handleCreateNewSprint = async () => {
    if (!user || !objective.trim()) return;

    setLoading(true);
    try {
      // Create new sprint
      const { data: sprint, error: sprintError } = await supabase
        .from('prompt_architecture_sprints')
        .insert({
          creator_id: user.id,
          objective: objective,
          status: 'in_progress'
        })
        .select()
        .single();

      if (sprintError) throw sprintError;

      // Add KB to sprint's inform stage
      const { error: contentError } = await supabase
        .from('sprint_inform_content')
        .insert({
          sprint_id: sprint.id,
          contributor_id: user.id,
          content_type: 'kb_link',
          content_reference: kb.id
        });

      if (contentError) throw contentError;

      toast({
        title: 'Sprint Created',
        description: `"${objective}" sprint created with KB imported!`
      });

      onClose();
      navigate(`/prompt-architecture-sprint?sprint=${sprint.id}`);
    } catch (error) {
      console.error('Error creating sprint:', error);
      toast({
        title: 'Error',
        description: 'Failed to create sprint. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToExistingSprint = async () => {
    if (!user || !selectedSprintId) return;

    setLoading(true);
    try {
      // Add KB to selected sprint's inform stage
      const { error } = await supabase
        .from('sprint_inform_content')
        .insert({
          sprint_id: selectedSprintId,
          contributor_id: user.id,
          content_type: 'kb_link',
          content_reference: kb.id
        });

      if (error) throw error;

      const selectedSprint = sprints?.find(s => s.id === selectedSprintId);
      toast({
        title: 'KB Added to Sprint',
        description: `Added to: ${selectedSprint?.objective || 'Sprint'}`
      });

      onClose();
      navigate(`/prompt-architecture-sprint?sprint=${selectedSprintId}`);
    } catch (error) {
      console.error('Error adding to sprint:', error);
      toast({
        title: 'Error',
        description: 'Failed to add KB to sprint. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileEdit className="h-5 w-5" />
            Edit in IDEAS Sprint
          </DialogTitle>
          <DialogDescription>
            Import this knowledge base into a sprint for collaborative editing and enhancement.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'new' | 'existing')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new">
              <Plus className="h-4 w-4 mr-2" />
              New Sprint
            </TabsTrigger>
            <TabsTrigger value="existing">Existing Sprint</TabsTrigger>
          </TabsList>

          <TabsContent value="new" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="objective">Sprint Objective</Label>
              <Input
                id="objective"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="What do you want to achieve with this sprint?"
              />
            </div>

            <Button
              onClick={handleCreateNewSprint}
              disabled={loading || !objective.trim()}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Sprint...
                </>
              ) : (
                'Create Sprint & Import KB'
              )}
            </Button>
          </TabsContent>

          <TabsContent value="existing" className="space-y-4">
            {sprintsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : sprints && sprints.length > 0 ? (
              <>
                <RadioGroup value={selectedSprintId || ''} onValueChange={setSelectedSprintId}>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {sprints.map((sprint) => (
                      <div
                        key={sprint.id}
                        className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer"
                        onClick={() => setSelectedSprintId(sprint.id)}
                      >
                        <RadioGroupItem value={sprint.id} id={sprint.id} />
                        <Label htmlFor={sprint.id} className="flex-1 cursor-pointer">
                          <div className="font-medium">{sprint.objective}</div>
                          <div className="text-sm text-muted-foreground">
                            Updated {new Date(sprint.updated_at).toLocaleDateString()}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>

                <Button
                  onClick={handleAddToExistingSprint}
                  disabled={loading || !selectedSprintId}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding to Sprint...
                    </>
                  ) : (
                    'Add KB to Selected Sprint'
                  )}
                </Button>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No in-progress sprints found.</p>
                <p className="text-sm mt-2">Create a new sprint to get started.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
